import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const FIELDS = [
  ['legal_first_name', 'Legal first name'], ['middle_name', 'Middle name'], ['surname', 'Surname'], ['preferred_name', 'Preferred name'],
  ['date_of_birth', 'Date of birth', 'date'], ['mobile', 'Mobile'], ['residential_address', 'Residential address'], ['postal_address', 'Postal address'],
  ['occupation', 'Occupation'], ['employment', 'Employment'], ['tax_residency', 'Tax residency'], ['marital_status', 'Marital status'],
  ['spouse_name', 'Spouse name'], ['spouse_date_of_birth', 'Spouse date of birth', 'date'], ['spouse_income', 'Spouse taxable income'],
];

const SecureForm = ({ title, testid, fields, path, onDone }) => {
  const [v, setV] = useState({});
  const send = async () => {
    try { await api.post(path, v); toast.success('Sent to your accountant for approval'); setV({}); onDone(); } catch (e) { toast.error(e.response?.data?.detail || 'Please check the details'); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-sm font-medium flex items-center gap-2"><Lock className="w-3.5 h-3.5" />{title}</p>
      <div className="grid sm:grid-cols-3 gap-2">
        {fields.map(([k, l, t]) => <Input key={k} type={t || 'text'} autoComplete="off" placeholder={l} value={v[k] || ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`${testid}-${k}`} />)}
      </div>
      <Button size="sm" variant="outline" onClick={send} data-testid={`${testid}-submit`}>Submit for approval</Button>
    </div>
  );
};

export const PersonalDetails = () => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [email, setEmail] = useState('');
  const load = useCallback(() => api.get('/api/portal/my-details').then(({ data }) => { setD(data); setForm(data.personal || {}); }), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  if (!d.applicable) return <p className="text-sm text-muted-foreground" data-testid="personal-not-applicable">{d.message}</p>;

  const save = async () => {
    const changed = Object.fromEntries(Object.entries(form).filter(([k, v]) => (v || null) !== (d.personal[k] || null)));
    try {
      const { data } = await api.put('/api/portal/my-details', { ...changed, _version: d.version });
      toast.success(`${data.applied.length ? 'Saved' : 'Nothing saved directly'}${data.pending ? ' · some changes need approval' : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const changeEmail = async () => {
    try { await api.post('/api/portal/my-details/email', { email }); toast.success('Check your new inbox to confirm the address'); setEmail(''); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not request change'); }
  };

  return (
    <Card data-testid="personal-details">
      <CardHeader>
        <CardTitle className="text-base">Personal details</CardTitle>
        <p className="text-sm text-muted-foreground">Preferred name, mobile, postal address and occupation update straight away. Other changes go to your accountant for approval.</p>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(([k, l, t]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs flex items-center gap-2">{l}{d.pending_fields.includes(k) && <Badge variant="secondary">Pending approval</Badge>}
                {!d.low_risk_fields.includes(k) && <Lock className="w-3 h-3 text-muted-foreground" />}</Label>
              <Input type={t || 'text'} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`personal-${k}`} />
            </div>
          ))}
        </div>
        <Button onClick={save} data-testid="personal-save">Save changes</Button>
        <div className="space-y-2">
          <div className="text-sm">Login email: <span className="font-mono" data-testid="personal-email">{d.email}</span>{d.pending_fields.includes('email') && <Badge className="ml-2" variant="secondary">Change pending</Badge>}</div>
          <div className="flex gap-2 max-w-md">
            <Input type="email" placeholder="New email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="personal-new-email" />
            <Button variant="outline" onClick={changeEmail} data-testid="personal-change-email">Change email</Button>
          </div>
        </div>
        <p className="text-sm">Refund bank account: <span className="font-mono" data-testid="personal-bank">{d.bank ? `${d.bank.account_name} ${d.bank.bsb} ${d.bank.account_number}` : 'Not provided'}</span></p>
        <SecureForm title="Update refund bank account" testid="bank-form" path="/api/portal/my-details/bank" onDone={load}
          fields={[['account_name', 'Account name'], ['bsb', 'BSB'], ['account_number', 'Account number']]} />
        <p className="text-sm">Tax file number: <span data-testid="personal-tfn">{d.tfn_on_file ? 'On file (hidden)' : 'Not provided'}</span></p>
        <SecureForm title="Provide or update your TFN" testid="tfn-form" path="/api/portal/my-details/tfn" onDone={load} fields={[['tfn', 'Tax file number', 'password']]} />
      </CardContent>
    </Card>
  );
};
